///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import {
	Badge,
	CurrentConditionsSection,
	formatTimeAgo,
	InfoField,
	MoreSection
} from "@/components/controls/anchored-panel/panelAtoms"
import { fireAcreage, isFireActive, type WildfireProperties } from "@/earthbrowser/dataset-distribution/wildfires/WildfiresTable"
import { formatFireArea } from "@/earthbrowser/utils/unitFormatters"

export function AnchoredWildfireInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "wildfire" ? data : null}
			fallbackTitle={content => content.wildfire.name ?? "Wildfire"}
			accentBorder="border-rose-400/20"
			pill="bg-rose-400/90"
		>
			{(content, ctx) => <WildfireBody wildfire={content.wildfire} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

function WildfireBody({ wildfire, ctx }: { wildfire: WildfireProperties; ctx: PanelShellContext }) {
	const active = isFireActive(wildfire)
	const acres = fireAcreage(wildfire)
	const containedPct = wildfire.percent_contained
	const discoveredDate = wildfire.discovered ? new Date(wildfire.discovered) : null
	const updatedDate = new Date(wildfire.date_current)
	const location = [wildfire.county ? `${wildfire.county} County` : null, wildfire.state]
		.filter(Boolean)
		.join(", ")

	return (
		<>
			<div className="space-y-1">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="text-xl font-bold text-white leading-tight">
						{ctx.title}
					</span>
					<Badge colorClass={active
						? "bg-rose-500/20 text-rose-300 border border-rose-400/40"
						: "bg-slate-500/20 text-slate-300 border border-slate-400/40"
					}>
						{active ? "Active" : "Contained"}
					</Badge>
				</div>
				{location && (
					<div className="text-sm text-slate-200 leading-snug">
						{location}
					</div>
				)}
			</div>

			{acres != null && (
				<div className="text-2xl font-light text-white leading-none">
					{formatFireArea(acres)}
					<span className="ml-2 text-xs text-slate-400 font-normal">burned</span>
				</div>
			)}

			{containedPct != null && (
				<div className="space-y-1">
					<div className="flex items-center justify-between text-xs">
						<span className="text-slate-400">Containment</span>
						<span className="text-slate-200 font-semibold">{Math.round(containedPct)}%</span>
					</div>
					<div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
						<div
							className="h-full rounded-full bg-rose-400"
							style={{ width: `${Math.min(100, Math.max(0, containedPct))}%` }}
						/>
					</div>
				</div>
			)}

			<MoreSection compact={ctx.compact}>
				<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-2">
					<div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
						{discoveredDate && (
							<InfoField label="Discovered" value={formatTimeAgo(discoveredDate)} />
						)}
						{wildfire.fire_cause && (
							<InfoField label="Cause" value={wildfire.fire_cause} />
						)}
						{wildfire.fire_behavior && (
							<InfoField label="Behavior" value={wildfire.fire_behavior} />
						)}
						{wildfire.personnel != null && wildfire.personnel > 0 && (
							<InfoField label="Personnel" value={wildfire.personnel.toLocaleString()} />
						)}
					</div>

					<div className="text-[10px] text-slate-500 pt-1 border-t border-white/5">
						Perimeter updated {formatTimeAgo(updatedDate)} &bull; Source: NIFC
					</div>
				</div>

				<CurrentConditionsSection
					lat={wildfire.centroid_lat}
					lon={wildfire.centroid_lon}
					accentClass="text-rose-400"
					errorClass="text-rose-400"
				/>
			</MoreSection>
		</>
	)
}
